import Axios, {
	type AxiosError,
	AxiosHeaders,
	type AxiosResponse,
	CanceledError,
	type InternalAxiosRequestConfig,
} from 'axios';
import { t } from '@/i18n';
import { ensureFreshToken, redirectToLogin } from './keycloak-session';

// Custom type to extend Axios request config with a flag to prevent infinite retry loops
type AuthenticatedRequestConfig = InternalAxiosRequestConfig & {
	_retryAfterRefresh?: boolean;
};

type FailedRequestQueueItem = {
	resolve: (token: string) => void;
	reject: (error: unknown) => void;
};

let isRefreshing = false;
const failedRequestQueue: FailedRequestQueueItem[] = [];

/**
 * Processes the queue of failed requests that occurred during a token refresh. If the token refresh was successful, it resolves all queued requests with the new token. If the token refresh failed, it rejects all queued requests with the error. This function is called after an attempt to refresh the token, and it ensures that all requests that were waiting for a fresh token are properly handled based on the outcome of the refresh attempt.
 * @param error - The error that occurred during token refresh, if any. If the refresh was successful, this will be null.
 * @param token - The new token obtained from a successful refresh, or null if the refresh failed.
 * @returns void
 */
function processFailedQueue(error: unknown, token: string | null): void {
	while (failedRequestQueue.length > 0) {
		const queuedRequest = failedRequestQueue.shift();
		// If there is no queued request, continue to the next iteration
		if (!queuedRequest) {
			continue;
		}

		// If there was an error during token refresh, reject the queued request with the error
		if (error || !token) {
			queuedRequest.reject(error || new Error(t('auth.errors.tokenRefreshFailed')));
			continue;
		}
		// If the token was refreshed successfully, resolve the queued request with the new token
		queuedRequest.resolve(token);
	}
}

// Create an Axios instance with the base URL from environment variables
export const axios = Axios.create({
	baseURL: import.meta.env.VITE_API_BASE_URL,
	timeout: 30_000,
});

// Request interceptor to add the Authorization header with a fresh token before each request
const authRequestInterceptor = async (config: InternalAxiosRequestConfig) => {
	// Ensure the token is fresh before making the API request
	const token = await ensureFreshToken(30);

	// If we can't get a token, redirect to login and don't send the request: it would
	// only come back 401, force another refresh and redirect a second time. Cancelled
	// rather than failed, so error toasts can ignore it while the page navigates away.
	if (!token) {
		redirectToLogin();
		throw new CanceledError('No access token; redirecting to login.');
	}

	// Set the Authorization header with the fresh token if available
	const headers = AxiosHeaders.from(config.headers);
	headers.set('ngrok-skip-browser-warning', 'true');
	if (token) {
		headers.set('Authorization', `Bearer ${token}`);
	}
	config.headers = headers;
	return config;
};

const responseInterceptor = (response: AxiosResponse) => {
	return response;
};

const errorInterceptor = async (error: AxiosError) => {
	// Cancelled on purpose (no token, or an aborted signal): nothing to handle or log.
	if (Axios.isCancel(error)) {
		return Promise.reject(error);
	}
	// Handle errors globally
	if (error.response) {
		// If the error is a 401 Unauthorized, attempt to refresh the token and retry the request
		if (error.response.status === 401) {
			const originalRequest = error.config as AuthenticatedRequestConfig | undefined;

			// If there's no original request or if it has already been retried after a refresh, redirect to login to prevent infinite retry loops
			if (!originalRequest || originalRequest._retryAfterRefresh) {
				console.error('Unauthorized access - redirecting to login');
				redirectToLogin();
				return Promise.reject(error);
			}
			// Mark the original request to indicate that it has been retried after a refresh attempt
			originalRequest._retryAfterRefresh = true;

			// If a token refresh is already in progress, queue the request to be retried after the refresh completes
			if (isRefreshing) {
				return new Promise<string>((resolve, reject) => {
					failedRequestQueue.push({ resolve, reject });
				})
					.then((token) => {
						const headers = AxiosHeaders.from(originalRequest.headers);
						headers.set('Authorization', `Bearer ${token}`);
						originalRequest.headers = headers;
						return axios(originalRequest);
					})
					.catch((refreshError: unknown) => {
						console.error('Unauthorized access - queued request failed after refresh', refreshError);
						redirectToLogin();
						return Promise.reject(error);
					});
			}
			// If no token refresh is in progress, start the refresh process
			isRefreshing = true;
			try {
				// Attempt to refresh the token. If successful, retry the original request with the new token.
				const token = await ensureFreshToken(0, true);

				// If we can't get a new token, redirect to login
				if (!token) {
					redirectToLogin();
					return Promise.reject(error);
				}

				// Process any queued requests that were waiting for the token refresh to complete
				processFailedQueue(null, token);

				// Retry the original request with the new token
				const headers = AxiosHeaders.from(originalRequest.headers);
				headers.set('Authorization', `Bearer ${token}`);
				originalRequest.headers = headers;
				return axios(originalRequest);
			} catch (refreshError) {
				// If the token refresh fails, reject all queued requests and redirect to login
				processFailedQueue(refreshError, null);
				console.error('Unauthorized access - token refresh failed', refreshError);
				redirectToLogin();
				return Promise.reject(error);
			} finally {
				isRefreshing = false;
			}
		}
		// Other HTTP errors (403/4xx/5xx) propagate to the caller; the query cache and
		// mutation onError handlers own the user-facing error toast (avoids double-toasting).
	} else if (error.request) {
		// Request was made but no response received (network error/timeout). The caller
		// (query cache / mutation onError) surfaces this to the user.
		console.error('No response received from API request:', error.message);
	} else {
		console.error('Error setting up API request:', error.message);
	}
	return Promise.reject(error);
};

// Add interceptors to the axios instance
axios.interceptors.request.use(authRequestInterceptor);
axios.interceptors.response.use(responseInterceptor, errorInterceptor);

/**
 * Pulls the backend-provided error message (NestJS error shape, possibly proxied
 * from an external service like the tile server) out of a failed request, if any.
 */
export function extractRequestErrorMessage(error: unknown): string | null {
	if (!Axios.isAxiosError(error)) return null;
	const message = (error.response?.data as { message?: string | string[] } | undefined)?.message;
	if (Array.isArray(message)) return message.join('; ');
	return message ?? null;
}
